// 상점·가방 아이템 아이콘 경로(public/assets/ui/items/, 256×256 투명 WebP). 2026-10-27 GPT 콜라주 회차(tools/concept/jobs/item-icons.codex.json).
// 마을 장비: gear_t<마을>_<부위|무기 종류>, 소모품: 품목 id 그대로(hp_s·elixir_str·scroll_return…), 보석: gem_<색>_<등급 0..4>.
// 그림이 없으면(새 품목) 호출부가 아이콘 칸을 비운다(img onerror). DOM·three import 금지(순수 경로 계산).
import type { EquipSlot, HeroId } from '../shared/content/types';
import { GEM_COLORS, gemColorOf, gemTierOf } from '../shared/content/consumables';

/** 영웅 → 무기 그림 종류 */
const WEAPON_ICON: Readonly<Partial<Record<HeroId, string>>> = { kael: 'sword', lyra: 'bow', seren: 'staff', brann: 'hammer', lumen: 'staff' };
/** 마을 장비 그림이 있는 마을 */
export const ICON_TOWNS: readonly number[] = [1, 2, 3, 4, 5, 6];
/** 목걸이 그림이 있는 마을 */
const AMULET_TOWNS: readonly number[] = [1, 3];

/** 마을 장비 품목(마을·부위) → 그림 경로(assetUrl 전). 무기는 지금 영웅의 무기 종류 */
export function townGearIconPath(town: number, slot: EquipSlot, hero: HeroId): string | null {
  if (!ICON_TOWNS.includes(town)) return null;
  // 마을⑤⑥(3개월 C2): 전용 마을 그림이 없어 그 지역 드롭 희귀 그림을 쓴다(상점 장비는 희귀 등급, 목걸이 포함)
  if (town >= 5) return `ui/items/drop_r${town}_${slot === 'weapon' ? 'weapon_' + (hero === 'lumen' ? 'seren' : hero) : slot}_r.webp`;
  // 목걸이 그림은 ①·③만 있다: 없는 마을은 가장 가까운 단계(동률이면 낮은 쪽)로 대신해 카드가 그림 없이 남지 않게 한다
  if (slot === 'amulet') return `ui/items/gear_t${AMULET_TOWNS.reduce((b, t) => Math.abs(t - town) < Math.abs(b - town) ? t : b)}_amulet.webp`;
  const part = slot === 'weapon' ? WEAPON_ICON[hero] ?? 'sword' : slot;
  return `ui/items/gear_t${town}_${part}.webp`;
}

/** 상점 장비 정의 id(eq_shop_t<마을>_<부위>[_<영웅>]) → 그림 경로. 상점 장비가 아니면 null */
export function shopDefIconPath(defId: string): string | null {
  const m = /^eq_shop_t(\d+)_(weapon|helm|armor|gloves|boots|amulet)(?:_(kael|lyra|seren|brann|lumen))?$/.exec(defId);
  if (!m) return null;
  return townGearIconPath(Number(m[1]), m[2] as EquipSlot, (m[3] as HeroId | undefined) ?? 'kael');
}

/** 소모품·재료 그림이 있는 품목 id */
export const CONS_ICON_IDS: readonly string[] = [
  'hp_s', 'hp_m', 'hp_l', 'mp_s', 'mp_m', 'mp_l', 'elixir_str', 'elixir_wall', 'elixir_luck', 'elixir_wis',
  'scroll_return', 'feather_revive', 'scroll_protect', 'stone_low', 'stone_mid', 'stone_high', 'stone_top',
];
/** 소모품 id → 그림 경로(없으면 null) */
export function consIconPath(id: string): string | null {
  return CONS_ICON_IDS.includes(id) ? `ui/items/${id}.webp` : null;
}

/** 보석 코드 → 그림 경로(색 id + 등급 0..4) */
export function gemIconPath(code: number): string | null {
  const c = GEM_COLORS[gemColorOf(code)];
  if (!c) return null;
  return `ui/items/gem_${c.id}_${gemTierOf(code) - 1}.webp`;
}

// ── 드롭·세트 장비(2026-10-27 3개월 분량 회차, tools/concept/jobs/gear-icons.codex.json) ──
// 지역 드롭: eq_r<r>_<부위> → drop_r<r>_<부위>, 무기 eq_r<r>_weapon_<영웅> → drop_r<r>_weapon_<영웅>
// 세트: eq_<세트 id>_<부위> → <세트 id>_<부위> (세트 id가 set_moss면 set_moss_helm)
const DROP_RE = /^eq_r([1-6])_(weapon_(?:kael|lyra|seren|brann|lumen)|helm|armor|gloves|boots|amulet)$/;
const SET_RE = /^eq_(set_[a-z]+)_(helm|armor|gloves|boots)$/;

/**
 * 등급별 드롭 그림 접미사(2026-10-27 대표님: 낮은 등급은 덜 화려한 그림을 따로). 일반 _c, 희귀 _r,
 * 영웅·전설은 접미사 없는 그림(전설은 칸에 금테·badge_legend), 세트 등급은 세트 정의 그림을 쓴다
 */
export function dropGradeSuffix(grade: string | undefined): '' | '_c' | '_r' {
  return grade === 'common' ? '_c' : grade === 'rare' ? '_r' : '';
}
/** 지역 드롭 정의 id(+개체 등급) → 그림 경로(드롭 정의가 아니면 null). 등급을 안 주면 접미사 없는(영웅 등급) 그림 */
export function dropDefIconPath(defId: string, grade?: string): string | null {
  const m = DROP_RE.exec(defId);
  // 루멘은 지팡이(세렌과 같은 그림) — 전용 그림이 생기면 이 줄만 지운다
  return m ? `ui/items/drop_r${m[1]}_${m[2] === 'weapon_lumen' ? 'weapon_seren' : m[2]}${dropGradeSuffix(grade)}.webp` : null;
}
/** 세트 정의 id → 그림 경로(세트 정의가 아니면 null) */
export function setDefIconPath(defId: string): string | null {
  const m = SET_RE.exec(defId);
  return m ? `ui/items/${m[1]}_${m[2]}.webp` : null;
}
/** 세트 id + 부위 → 그림 경로(세트 창 4부위 칸) */
export function setPieceIconPath(setId: string, slot: EquipSlot): string {
  return `ui/items/${setId}_${slot}.webp`;
}
/** 정의 id → 그 정의 전용 그림(상점·드롭·세트). 전용 그림 규칙이 없으면 null(호출부가 단계 그림을 쓴다). 드롭은 grade로 등급 그림을 고른다 */
export function gearDefIconPath(defId: string, grade?: string): string | null {
  return shopDefIconPath(defId) ?? dropDefIconPath(defId, grade) ?? setDefIconPath(defId);
}
/**
 * 정의 id + 개체 등급 → 전용 그림 후보(앞에서부터 시도). 드롭 일반·희귀는 등급 그림 → 접미사 없는 그림 순서
 * (등급 그림이 아직 없을 때 대체). 전용 규칙이 없으면 빈 배열
 */
export function gearDefIconCandidates(defId: string, grade?: string): string[] {
  const first = gearDefIconPath(defId, grade);
  if (!first) return [];
  const plain = dropDefIconPath(defId);
  return plain && plain !== first ? [first, plain] : [first];
}

/** 등급 배지(아이콘 모서리): 세트·전설만 */
export const BADGE_SET = 'ui/items/badge_set.webp';
export const BADGE_LEGEND = 'ui/items/badge_legend.webp';
export function gradeBadgePath(grade: string): string | null {
  return grade === 'legend' ? BADGE_LEGEND : grade === 'set' ? BADGE_SET : null;
}
